import { ApiErrorType } from '@/modules/core/enums/api-error-type.ts'
import type { ApiErrorDetails } from '@/modules/core/models/api-error-details.ts'

export class ApiError extends Error {
  public readonly type: ApiErrorType
  public readonly statusCode?: number
  public readonly retryable: boolean
  public readonly userMessage: string
  public readonly originalError?: any

  constructor(details: ApiErrorDetails) {
    super(details.message)
    this.type = details.type
    this.statusCode = details.statusCode
    this.retryable = details.retryable
    this.userMessage = details.userMessage
    this.originalError = details.originalError

    Object.setPrototypeOf(this, ApiError.prototype)
  }

  static fromAxiosError(error: any): ApiError {
    if (error.message === 'OFFLINE_MODE') {
      return new ApiError({
        type: ApiErrorType.OFFLINE_ERROR,
        message: 'Device is offline',
        retryable: true,
        userMessage:
          'You appear to be offline. Your request will be processed when connection is restored.',
      })
    }

    if (!error.response) {
      return new ApiError({
        type: ApiErrorType.NETWORK_ERROR,
        message: error.message || 'Network error occurred',
        retryable: true,
        userMessage: 'Unable to connect to the server. Please check your internet connection.',
        originalError: error,
      })
    }

    const status = error.response.status
    const data = error.response.data

    if (status === 401) {
      return new ApiError({
        type: ApiErrorType.AUTHENTICATION_ERROR,
        statusCode: status,
        message: 'Authentication failed',
        retryable: false,
        userMessage: 'Please log in again to continue.',
        originalError: error,
      })
    }

    if (status === 403) {
      return new ApiError({
        type: ApiErrorType.AUTHORIZATION_ERROR,
        statusCode: status,
        message: 'Access denied',
        retryable: false,
        userMessage: 'You do not have permission to perform this action.',
        originalError: error,
      })
    }

    if (status === 422 && data?.errors) {
      return new ApiError({
        type: ApiErrorType.VALIDATION_ERROR,
        statusCode: status,
        message: 'Validation failed',
        retryable: false,
        userMessage: 'Please check your input and try again.',
        originalError: error,
      })
    }

    if (status >= 500) {
      return new ApiError({
        type: ApiErrorType.SERVER_ERROR,
        statusCode: status,
        message: 'Server error occurred',
        retryable: true,
        userMessage: 'Something went wrong on our end. Please try again in a moment.',
        originalError: error,
      })
    }

    return new ApiError({
      type: ApiErrorType.CLIENT_ERROR,
      statusCode: status,
      message: data?.message || 'Request failed',
      retryable: false,
      userMessage: data?.message || 'Something went wrong. Please try again.',
      originalError: error,
    })
  }
}
